import { useCallback, useMemo, useState } from 'react';
import { Code2, Pencil, Check, X, Maximize2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Checkbox } from '../../../../components/ui/checkbox';
import { looksLikeJsonString } from '../../common/utils/jsonDetection';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import { lookupForeignKey, type ForeignKeyMap } from '../hooks/useTableMetadata';
import { ForeignKeyPopover } from './ForeignKeyPopover';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';
import { getRowValueByColumnName } from '../utils/rowValueUtils';
import { ExpandedValueDialog } from './ExpandedValueDialog';
import {
  shouldShowExpandIcon,
  detectContentMode,
  formatValueForExpandedEdit,
  type ExpandedEditMode,
} from '../utils/expandedEditDetection';
import { safeToISOString, safeToDatetimeLocal } from '../../../../utils/dateUtils';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { findLooseRelationshipsForColumn } from '../../common/utils/looseFkResolver';
import { JsonLooseFkBadges } from '../../common/components/filters/JsonLooseFkBadges';
import { resolveCellEditVariant } from '../../common/components/cell-edit/cellEditVariant';
import { SmartCellInput } from '../../common/components/cell-edit/SmartCellInput';
import { categorizeEnumColumn } from '../../common/utils/enumColumnCategorization';
import type { LooseRelationship } from '../../../../types/electron';
import type { DatabaseEngineId } from '../../../../core/engines';

type RowDetailsDataTabProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly row: Record<string, unknown>;
  readonly columns: TableExplorerColumn[];
  readonly foreignKeyMap?: ForeignKeyMap;
  readonly canQuickEdit?: boolean;
  readonly primaryKeyColumns?: string[];
  readonly onQuickEdit?: (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => void;
  /** Source schema for the explored table (used by FK popover for loose-relationship cleanup). */
  readonly sourceSchema?: string;
  /** Source table name for the explored table (used by FK popover for loose-relationship cleanup). */
  readonly sourceTable?: string;
  /** Engine id — required for the smart cell-edit pickers (FK / enum / boolean). */
  readonly engineId?: DatabaseEngineId;
  /**
   * Declared and native enum column-name sets for the explored table. Passed in
   * by the host so we don't re-derive the categorization from MetaInfo here.
   * Both default to empty sets.
   */
  readonly declaredEnumColumns?: ReadonlySet<string>;
  readonly nativeEnumColumns?: ReadonlySet<string>;
};

function formatScalarValue(value: unknown): string {
  if (value instanceof Date) return safeToISOString(value);
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

function getInputType(dataType: string): 'text' | 'number' | 'boolean' | 'datetime' {
  const lowerType = dataType.toLowerCase();
  if (lowerType.includes('bit') || lowerType.includes('bool')) return 'boolean';
  if (
    lowerType.includes('int') ||
    lowerType.includes('decimal') ||
    lowerType.includes('numeric') ||
    lowerType.includes('float') ||
    lowerType.includes('real') ||
    lowerType.includes('money')
  ) {
    return 'number';
  }
  if (lowerType.includes('date') || lowerType.includes('time') || lowerType.includes('timestamp')) {
    return 'datetime';
  }
  return 'text';
}

function formatValueForInput(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) return safeToDatetimeLocal(value);
    return JSON.stringify(value);
  }
  return String(value);
}

function parseInputValue(inputValue: string, inputType: 'text' | 'number' | 'boolean' | 'datetime'): unknown {
  if (inputValue === '' || inputValue === 'NULL') return null;
  switch (inputType) {
    case 'number': {
      const num = Number(inputValue);
      return isNaN(num) ? inputValue : num;
    }
    case 'boolean':
      return inputValue === 'true' || inputValue === '1';
    case 'datetime':
      return inputValue ? new Date(inputValue) : null;
    default:
      return inputValue;
  }
}

function tryFormatJson(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'string') {
    if (!looksLikeJsonString(value)) return null;
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return JSON.stringify(value);
    }
  }

  return null;
}

export function RowDetailsDataTab({
  connectionId,
  database,
  row,
  columns,
  foreignKeyMap,
  canQuickEdit = false,
  primaryKeyColumns = [],
  onQuickEdit,
  sourceSchema,
  sourceTable,
  engineId,
  declaredEnumColumns,
  nativeEnumColumns,
}: RowDetailsDataTabProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  // State for expanded value dialog
  const [expandedEdit, setExpandedEdit] = useState<{
    columnName: string;
    value: unknown;
    mode: ExpandedEditMode;
    dataType: string;
    readOnly: boolean;
  } | null>(null);

  // Loose-FK declarations on this `(schema, table, *)` — used by the JSON cell
  // path so we can render the per-path chip cluster *next to* the value
  // instead of wrapping the entire JSON blob in a ForeignKeyPopover (which
  // would feed the engine a JSON document as the lookup key and trigger
  // "invalid input syntax for type integer" on Postgres).
  const { data: metaInfo } = useMetaInfo(connectionId, database);

  // Derive enum sets for the smart cell-edit picker. The host can pre-compute
  // these; otherwise we fall back to running `categorizeEnumColumn` over the
  // visible columns.
  const enumColumnSets = useMemo(() => {
    if (declaredEnumColumns && nativeEnumColumns) {
      return { declared: declaredEnumColumns, native: nativeEnumColumns };
    }
    const declared = new Set<string>();
    const native = new Set<string>();
    if (engineId && sourceSchema !== undefined && sourceTable !== undefined) {
      for (const col of columns) {
        const cat = categorizeEnumColumn(
          {
            engine: engineId,
            schema: sourceSchema,
            table: sourceTable,
            name: col.name,
            dataType: col.dataType,
            isNativeEnumType: col.isNativeEnumType,
          },
          metaInfo,
        );
        if (cat === 'declared-enum') declared.add(col.name);
        else if (cat === 'native-enum') native.add(col.name);
      }
    }
    return { declared, native };
  }, [declaredEnumColumns, nativeEnumColumns, engineId, sourceSchema, sourceTable, columns, metaInfo]);
  const jsonPathRelsByColumn = useMemo(() => {
    const map = new Map<string, LooseRelationship[]>();
    // `sourceSchema` is `''` on schemaless engines (Kusto/ADX) — present, not
    // missing; `== null` keeps JSON-path loose-FK chips resolving there.
    if (sourceSchema == null || !sourceTable) return map;
    for (const col of columns) {
      const rels = findLooseRelationshipsForColumn(
        metaInfo?.looseRelationships,
        sourceSchema,
        sourceTable,
        col.name,
      ).filter((r) => r.sourcePath);
      if (rels.length > 0) map.set(col.name, rels);
    }
    return map;
  }, [metaInfo, columns, sourceSchema, sourceTable]);

  const primaryKeySet = useMemo(
    () => new Set(primaryKeyColumns.map(normalizeColumnName)),
    [primaryKeyColumns]
  );

  const getPrimaryKeyFromRow = useCallback((): Record<string, unknown> => {
    const rowKeyByNormalized = new Map<string, string>();
    for (const key of Object.keys(row)) {
      rowKeyByNormalized.set(normalizeColumnName(key), key);
    }
    const pk: Record<string, unknown> = {};
    for (const col of primaryKeyColumns) {
      const rowKey = rowKeyByNormalized.get(normalizeColumnName(col));
      pk[col] = rowKey ? row[rowKey] : row[col];
    }
    return pk;
  }, [row, primaryKeyColumns]);

  const handleStartEdit = useCallback((columnName: string, value: unknown) => {
    setEditingField(columnName);
    setEditValue(formatValueForInput(value));
  }, []);

  const handleOpenExpandedEdit = useCallback(
    (
      columnName: string,
      value: unknown,
      mode: ExpandedEditMode,
      dataType: string,
      options?: { readOnly?: boolean }
    ) => {
      setExpandedEdit({ columnName, value, mode, dataType, readOnly: options?.readOnly ?? false });
    },
    []
  );

  const handleExpandedEditConfirm = useCallback((newValue: string) => {
    if (!expandedEdit) return;

    // Read-only dialog: just close (Save is not shown, but Ctrl/Cmd+Enter still triggers confirm).
    if (expandedEdit.readOnly) {
      setExpandedEdit(null);
      return;
    }

    if (!onQuickEdit) return;

    const { columnName, value: oldValue } = expandedEdit;
    const primaryKey = getPrimaryKeyFromRow();

    // Convert empty string to null
    const finalValue = newValue === '' ? null : newValue;

    // Close the expanded edit dialog FIRST
    setExpandedEdit(null);

    // Delay opening the confirmation dialog to allow the expanded edit
    // dialog's backdrop to fully unmount (prevents nested dialog blocking)
    setTimeout(() => {
      onQuickEdit(primaryKey, columnName, oldValue, finalValue);
    }, 250);
  }, [expandedEdit, getPrimaryKeyFromRow, onQuickEdit]);

  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue('');
  }, []);

  const handleConfirmEdit = useCallback(
    (columnName: string, dataType: string, oldValue: unknown) => {
      if (!onQuickEdit) return;
      const inputType = getInputType(dataType);
      const newValue = parseInputValue(editValue, inputType);
      const primaryKey = getPrimaryKeyFromRow();
      onQuickEdit(primaryKey, columnName, oldValue, newValue);
      setEditingField(null);
      setEditValue('');
    },
    [editValue, getPrimaryKeyFromRow, onQuickEdit]
  );

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden rounded-md border border-border bg-background/60">
        <div className="flex-1 min-h-0 overflow-auto p-2">
          <div
            className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-start"
            data-testid="row-details-data-grid"
          >
            {columns.map((col) => {
              // By the exact column name: `"ref id"` never takes `"ref-id"`'s FK.
              const fkInfo = lookupForeignKey(foreignKeyMap, col.name);
              const value = getRowValueByColumnName(row, col.name);
              const isNull = value === null || value === undefined;
              const jsonFormatted = tryFormatJson(value);
              const displayValue = isNull
                ? 'NULL'
                : jsonFormatted ?? (typeof value === 'object' ? JSON.stringify(value) : formatScalarValue(value));

              const isPrimaryKeyColumn = primaryKeySet.has(normalizeColumnName(col.name));
              const isIdentityColumn = col.isIdentity || false;
              const isEditable = canQuickEdit && !isPrimaryKeyColumn && !isIdentityColumn;
              const isEditing = editingField === col.name;
              const inputType = getInputType(col.dataType);

              // JSON loose-FK awareness: if this column carries loose FKs on
              // JSON paths AND the value looks like JSON, render a per-path
              // chip cluster next to the action buttons and DO NOT wrap the
              // value text in `ForeignKeyPopover` (which would lookup the
              // whole JSON blob as if it were the FK key).
              const jsonPathRels = jsonPathRelsByColumn.get(col.name) ?? [];
              const hasJsonValue = jsonFormatted !== null;
              const useJsonPathFkChips = jsonPathRels.length > 0 && hasJsonValue && !isNull;

              // Truncate long / JSON content to 5 visible lines — the expand
              // button alongside is the escape hatch to the full
              // ExpandedValueDialog. Heuristic: line-clamp when the displayed
              // text has >5 lines or is longer than ~400 chars, or when the
              // expand button would render (which is the same trigger used
              // by the cluster's expand affordance).
              const displayLineCount = !isNull ? displayValue.split('\n').length : 0;
              const shouldClamp =
                !isNull && (
                  hasJsonValue ||
                  displayLineCount > 5 ||
                  displayValue.length > 400 ||
                  shouldShowExpandIcon(value, col)
                );
              const clampClass = shouldClamp ? 'line-clamp-5' : '';

              // Smart cell-edit decision — FK / enum / boolean pickers.
              // Falls back to the existing free-text path when 'plain'.
              const looseRelCandidates = (metaInfo?.looseRelationships ?? []).filter(
                (rel) =>
                  sourceSchema !== undefined &&
                  sourceTable !== undefined &&
                  rel.fromSchema === sourceSchema &&
                  rel.fromTable === sourceTable &&
                  rel.fromColumn === col.name &&
                  !rel.sourcePath,
              );
              const realFkTarget = fkInfo && !fkInfo.isLoose
                ? {
                    schema: fkInfo.referencedSchema ?? '',
                    table: fkInfo.referencedTable,
                    column: fkInfo.referencedColumn,
                  }
                : undefined;
              const smartVariant = engineId && connectionId && database && sourceSchema != null && sourceTable
                ? resolveCellEditVariant({
                    columnId: col.name,
                    dataType: col.dataType,
                    isRealForeignKey: !!realFkTarget,
                    realFkTarget,
                    declaredEnumColumnIds: enumColumnSets.declared,
                    nativeEnumColumnIds: enumColumnSets.native,
                    looseRelCandidates,
                    sourceRow: row,
                    nullable: col.nullable,
                  })
                : undefined;
              const useSmartEditor =
                isEditing &&
                smartVariant &&
                smartVariant.variant !== 'plain' &&
                engineId &&
                sourceSchema !== undefined &&
                sourceTable !== undefined;

              const valueNode = (() => {
                if (isEditing && useSmartEditor && smartVariant) {
                  return (
                    <SmartCellInput
                      variant={smartVariant}
                      initialValue={value}
                      connectionId={connectionId}
                      database={database}
                      engine={engineId!}
                      schema={sourceSchema!}
                      table={sourceTable!}
                      column={col.name}
                      dataType={col.dataType}
                      enumSource={enumColumnSets.declared.has(col.name) ? 'declared' : 'native'}
                      onCommit={(newValue) => {
                        if (!onQuickEdit) return;
                        const primaryKey = getPrimaryKeyFromRow();
                        onQuickEdit(primaryKey, col.name, value, newValue);
                        setEditingField(null);
                        setEditValue('');
                      }}
                      onCancel={handleCancelEdit}
                    />
                  );
                }
                if (isEditing) {
                  if (inputType === 'boolean') {
                    return (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={editValue === 'true' || editValue === '1'}
                          onCheckedChange={(checked) => {
                            setEditValue(checked ? 'true' : 'false');
                          }}
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleConfirmEdit(col.name, col.dataType, value)}
                            title="Confirm"
                            aria-label="Confirm edit"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            onClick={handleCancelEdit}
                            title="Cancel"
                            aria-label="Cancel edit"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="flex items-center gap-1">
                      <Input
                        type={inputType === 'datetime' ? 'datetime-local' : inputType === 'number' ? 'number' : 'text'}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleConfirmEdit(col.name, col.dataType, value);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCancelEdit();
                          }
                        }}
                        className="h-7 text-xs flex-1"
                        autoFocus
                        data-testid={`row-details-edit-input-${col.name}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleConfirmEdit(col.name, col.dataType, value)}
                        title="Confirm"
                        aria-label="Confirm edit"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                        onClick={handleCancelEdit}
                        title="Cancel"
                        aria-label="Cancel edit"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                }

                if (isNull) {
                  return <span className="text-muted-foreground/60 italic">NULL</span>;
                }

                // JSON columns with loose-FK on a path: render as plain
                // (clamped) value — the chip cluster in the action area
                // hosts the FK navigation per path, not the value blob.
                if (useJsonPathFkChips) {
                  return (
                    <span
                      data-testid={`row-details-value-${col.name}`}
                      className={`break-words whitespace-pre-wrap font-mono text-[11px] ${clampClass}`}
                    >
                      {displayValue}
                    </span>
                  );
                }

                if (fkInfo) {
                  return (
                    <ForeignKeyPopover
                      connectionId={connectionId}
                      database={database}
                      referencedSchema={fkInfo.referencedSchema || ''}
                      referencedTable={fkInfo.referencedTable}
                      referencedColumn={fkInfo.referencedColumn}
                      value={value}
                      isLoose={fkInfo.isLoose}
                      sourceSchema={sourceSchema}
                      sourceTable={sourceTable}
                      sourceColumn={col.name}
                    >
                      <span
                        data-testid={`row-details-value-${col.name}`}
                        className={`break-words whitespace-pre-wrap ${clampClass}`}
                      >
                        {displayValue}
                      </span>
                    </ForeignKeyPopover>
                  );
                }

                const isJsonLike = jsonFormatted !== null;
                return (
                  <span
                    data-testid={`row-details-value-${col.name}`}
                    className={`break-words whitespace-pre-wrap ${isJsonLike ? 'font-mono text-[11px]' : ''} ${clampClass}`}
                  >
                    {displayValue}
                  </span>
                );
              })();

              return (
                <div
                  key={col.name}
                  className="border-b border-divider p-2"
                  data-testid={`row-details-card-${col.name}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 min-w-0">
                        {col.isPrimaryKey && (
                          <span className="text-warning text-[10px]" title="Primary Key">
                            PK
                          </span>
                        )}
                        {fkInfo && (
                          <span
                            className={
                              fkInfo.isLoose
                                ? 'text-green-600 dark:text-green-400 text-[10px] italic'
                                : 'text-blue-500 text-[10px]'
                            }
                            title={fkInfo.isLoose ? 'Loose Foreign Key' : 'Foreign Key'}
                          >
                            FK
                          </span>
                        )}
                        <div className="text-xs font-medium truncate" title={col.name}>
                          {col.name}
                        </div>
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate" title={col.dataType}>
                        {col.dataType}
                        {col.nullable ? '' : ' NOT NULL'}
                      </div>
                      {useJsonPathFkChips && !isEditing && connectionId && database && sourceSchema != null && sourceTable && (
                        <div
                          className="flex items-center gap-1 mt-1 min-w-0"
                          data-testid={`row-details-fk-chips-${col.name}`}
                        >
                          <JsonLooseFkBadges
                            connectionId={connectionId}
                            database={database}
                            value={value}
                            sourceSchema={sourceSchema}
                            sourceTable={sourceTable}
                            sourceColumn={col.name}
                            rels={jsonPathRels}
                            surface="table_explorer"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-0.5">
                      {/* Edit button - for inline editing of simple values */}
                      {isEditable && !isEditing && !shouldShowExpandIcon(value, col) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Edit value"
                          aria-label="Edit value"
                          onClick={() => handleStartEdit(col.name, value)}
                          data-testid={`row-details-edit-btn-${col.name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* Expand edit button - for JSON/multiline content when editable */}
                      {isEditable && !isEditing && shouldShowExpandIcon(value, col) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Edit in expanded editor"
                          aria-label="Edit in expanded editor"
                          onClick={() => {
                            const mode = detectContentMode(value, col);
                            handleOpenExpandedEdit(col.name, value, mode, col.dataType);
                          }}
                          data-testid={`row-details-expand-edit-btn-${col.name}`}
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* View JSON button - for read-only JSON viewing when not editable */}
                      {jsonFormatted !== null && !isEditing && !isEditable && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="View JSON"
                          aria-label="View JSON"
                          onClick={() => handleOpenExpandedEdit(col.name, jsonFormatted, 'json', col.dataType, { readOnly: true })}
                        >
                          <Code2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 text-xs min-w-0">{valueNode}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Expanded Value Dialog for JSON/multiline text */}
      <ExpandedValueDialog
        open={expandedEdit !== null}
        onOpenChange={(open) => {
          if (!open) setExpandedEdit(null);
        }}
        initialValue={formatValueForExpandedEdit(expandedEdit?.value, expandedEdit?.mode ?? 'text')}
        initialMode={expandedEdit?.mode ?? 'text'}
        onConfirm={handleExpandedEditConfirm}
        title={`${expandedEdit?.readOnly ? 'View' : 'Edit'} ${expandedEdit?.columnName ?? 'Value'}`}
        columnDataType={expandedEdit?.dataType}
        readOnly={expandedEdit?.readOnly ?? false}
      />
    </>
  );
}
